/**
 * AgeConsentGate — the App-mounted host that turns the axios interceptor's
 * COPPA signals into UI. Mounts once near the app root (inside the
 * authenticated tree); subscribes to `ageConsentController` and renders the
 * DOB-capture modal and/or the under-13 limited-mode parent-invite overlay.
 *
 * It renders NOTHING until a gate fires, so it is invisible (and zero-cost
 * visually) for the common case (a 13+ user with a captured DOB). When the
 * interceptor calls `requestDob()` the controller fires `onRequestDob` with the
 * capture it opened → we mount <AgeGateModal> for that capture; on submit the
 * modal POSTs /auth/age and calls `onResolved`, which settles THAT capture
 * (`capture.resolve()`) and so releases every pending 426 to retry. Never the
 * controller's `resolveDob()`, which settles whichever capture is open (see
 * below). When the interceptor calls `showPlatformConsent()` we mount
 * <PlatformConsentScreen>.
 *
 * Two independent slots (DOB modal AND consent overlay can both be requested in
 * a session, though rarely at once). Both are <Modal>s, so whichever opened
 * last is on top, for the keyboard and for paint (components/ui/modalStack.js).
 * Opened together in one commit, the DOB modal renders last and so registers
 * last: the age question must be answered before egress is even evaluated.
 *
 * Each slot closes itself and nothing else. This host closes the DOB modal in
 * two ways only, and each settles the capture: the capture's resolve() or
 * reject(). Closing it without either (as the consent overlay's close once
 * did) leaves every request parked on the age question waiting for good, and
 * no later 426 can raise the modal again: the controller still holds the
 * unsettled capture. The modal also goes when this host unmounts (signing out
 * takes the whole signed-in tree); the unsubscribe below then fails the
 * capture — the controller does that itself, so it holds however the host
 * goes away.
 *
 * The modal's callbacks are bound to the capture it was rendered for, not to
 * "whatever is open": its save is async, so they can run after that capture
 * ended (this host unmounted mid-save, or a later capture opened in its
 * place). Then they settle nothing, and close no modal showing another.
 *
 * Wiring the controller↔component bridge in a single mounted host (rather than
 * threading callbacks through the tree) keeps the interceptor — which lives
 * outside React — able to drive UI from anywhere a gated request fires.
 */
import { useEffect, useState } from 'react';
import { subscribe } from './ageConsentController';
import AgeGateModal from './AgeGateModal';
import PlatformConsentScreen from './PlatformConsentScreen';

export default function AgeConsentGate({ currentUserId = null }) {
  // The capture the DOB modal is showing (handed over by onRequestDob), or
  // null when the modal is closed.
  const [dobCapture, setDobCapture] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);

  useEffect(() => {
    // Keep [] deps: a re-run unsubscribes, which fails the open capture and so
    // cancels every request held on it while its page is still mounted.
    const unsubscribe = subscribe({
      onRequestDob: (capture) => setDobCapture(capture),
      onShowPlatformConsent: () => setConsentOpen(true),
    });
    return unsubscribe;
  }, []);

  // Close the modal if it still shows `capture`, then settle that capture:
  // resolve (the DOB is saved; the interceptor retries the original requests)
  // or reject (dismissed; each gated call surfaces its own state). A late
  // callback for a capture that has ended does neither (see the header).
  const endDob = (capture, settle) => {
    setDobCapture((shown) => (shown === capture ? null : shown));
    settle();
  };

  const handleConsentClose = () => setConsentOpen(false);

  return (
    <>
      {consentOpen && (
        <PlatformConsentScreen
          onClose={handleConsentClose}
          currentUserId={currentUserId}
        />
      )}
      {/* The DOB modal renders LAST, so when both open in one commit it
          registers last on the modal stack and is on top — the age question
          gates whether egress is even evaluated, so it takes precedence. Open
          at different times, whichever opened later is on top. Its callbacks
          close over THIS render's capture: a save in flight keeps the one it
          was submitted under. */}
      {dobCapture && (
        <AgeGateModal
          onResolved={() => endDob(dobCapture, dobCapture.resolve)}
          onDismiss={() => endDob(dobCapture, dobCapture.reject)}
        />
      )}
    </>
  );
}
